import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {Col, Spinner, Table} from "reactstrap";
import qs from "qs";
import * as _ from "lodash";
import toastr from "toastr";

import CancelModal from "components/manageSessionModal/cancelModal";
import VoucherService from "services/voucher";
import SimpleTableBody from "components/simpleTable/simpleTableBody";
import parseIntFromStr from "helpers/parseIntFromStr";
import {ACTIVATION_CODE_LIMIT} from "constants/paginationLimits";
import queryParser from "helpers/queryStringParser";
import InfoModal from "components/manageSessionModal/infoModal";

import PaginationTableFooter from "../patientManagement/tableComponents/paginationFooter";
import TableHeader from "../patientManagement/tableComponents/tableHeader";
import {voucherTableHeader, structureWithActions} from "./tableComponents/vouchersDataStructure";
import NoCouponsPlaceholder from "./tableComponents/noCouponsPlaceholder";
import CreateCouponForm from "./voucherComponents/createCouponForm";

const SUCCESS_CODE_CREATION_MESSAGE = "New voucher code have been successfully generated";
const SUCCESS_CODE_UPDATING_MESSAGE = "Voucher code has been successfully updated";
const SUCCESS_CODE_DELETING_MESSAGE = "The voucher code has been deleted";

const Voucher = () => {
    const history = useHistory();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [vouchers, setVouchers] = useState([]);
    const [isVoucherModal, setIsVoucherModal] = useState(false);
    const [editVoucher, setEditVoucher] = useState(null);
    const [deleteVoucherId, setDeleteVoucherId] = useState(null);
    const [isOpenInvalidEmailsModal, setIsOpenInvalidEmailsModal] = useState(false);
    const [invalidEmailsList, setInvalidEmailsList] = useState([]);

    const searchQuery = queryParser(location.search)

    const setNewSearchQuery = (incomeQuery) => {
        const newQuery = {
            ...searchQuery,
            ...incomeQuery,
        }
        const query = qs.stringify(newQuery);
        history.push({
            search: query,
        });
    }

    const handleChangePage = ({ selected }) => {
        if (!isNaN(selected)) {
            setNewSearchQuery({ offset: selected * ACTIVATION_CODE_LIMIT });
        }
    }

    const editFn = useCallback((coupon) => {
        const {
            id,
            code,
            numberOfUses = "",
            startDate,
            endDate,
            hasAttachedEmails,
            isMultiple,
            discount,
        } = coupon;

        setEditVoucher({
            id,
            code,
            numberOfUses: String(numberOfUses),
            startDate,
            endDate,
            hasAttachedEmails,
            isMultiple,
            discount,
        })
    }, [setEditVoucher])

    const deleteFn = ({ id }) => {
        setDeleteVoucherId(id);
    }

    const activationCodeDataStructure = useMemo(() => structureWithActions(editFn, deleteFn), []);

    const handleCreateVoucher = useCallback(async (values) => {
        try {
            const response = await VoucherService.create(values);

            if (response.data?.invalidEmails?.length) {
                setIsOpenInvalidEmailsModal(true);
                setInvalidEmailsList(response.data?.invalidEmails)
            }

            toastr.success(SUCCESS_CODE_CREATION_MESSAGE);
            await getVouchers();

        } finally {
            setIsVoucherModal(false);
        }
    }, [setIsVoucherModal])

    const handleUpdateVoucher = useCallback(async (values) => {
        // eslint-disable-next-line no-unused-vars
        const { codesCount, id, ...rest } = values || {};

        try {
            const response = await VoucherService.update(id, rest);

            if (response.data?.invalidEmails?.length) {
                setIsOpenInvalidEmailsModal(true);
                setInvalidEmailsList(response.data?.invalidEmails)
            }

            toastr.success(SUCCESS_CODE_UPDATING_MESSAGE);
            await getVouchers();

        } finally {
            setEditVoucher(null);
        }
    }, [setEditVoucher, editVoucher])

    const handleDeleteVoucher = useCallback(async () => {
        try {
            await VoucherService.delete(deleteVoucherId);

            toastr.success(SUCCESS_CODE_DELETING_MESSAGE);
            await getVouchers();
        } finally {
            setDeleteVoucherId(null);
        }
    }, [deleteVoucherId])

    const getVouchers = useCallback(async () => {
        try {
            setIsLoading(true);
            const { data, pagination } = await VoucherService.getList({
                limit: ACTIVATION_CODE_LIMIT,
                offset: searchQuery.offset
            });
            setVouchers(data);
            setPagination(pagination);
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setVouchers, setPagination, searchQuery.offset])

    useEffect(() => {
        getVouchers();
    }, [getVouchers])

    return (
        <div className="page-content mx-2 pb-2">
            <div className="card justify-content-between pr-20px pl-20px pt-20px activate-code-page">
                <div className="d-flex flex-wrap justify-content-between align-items-end h-100 mb-4">
                    <Col sm={6} lg={4} className="p-0">
                        <label className="details-caption">
                            Voucher code management
                        </label>
                    </Col>
                    <Col sm={5} lg={4} className="p-0 header-button">
                        <button
                            className="btn btn-primary call-form-button w-100  mt-1 mt-sm-0"
                            onClick={() => setIsVoucherModal(true)}
                        >
                            Generate voucher code
                        </button>
                    </Col>
                </div>
                {isLoading &&
                    <div className="coupon-table d-flex justify-content-center align-items-center">
                        <Spinner className="mx-auto" color="info"/>
                    </div>}
                {!isLoading && vouchers.length > 0 && (
                    <>
                        <section className="activate-code-table-wrap">
                            <Table className="table mb-0">
                                <TableHeader columns={voucherTableHeader} clickCellAction={_.noop}/>
                                <SimpleTableBody data={vouchers} structure={activationCodeDataStructure}/>
                            </Table>
                        </section>
                        {!!pagination?.totalCount && (
                            <section className="mt-4">
                                <PaginationTableFooter
                                    total={pagination.totalCount}
                                    handleChangePage={handleChangePage}
                                    currentOffset={parseIntFromStr(searchQuery.offset)}
                                    limit={ACTIVATION_CODE_LIMIT}
                                />
                            </section>
                        )}
                    </>
                )}
                {!isLoading && !vouchers.length && <NoCouponsPlaceholder/>}
            </div>
            <CreateCouponForm
                isOpen={isVoucherModal}
                toggle={() => setIsVoucherModal(false)}
                handleSubmit={handleCreateVoucher}
            />
            <CreateCouponForm
                isOpen={!!editVoucher}
                toggle={() => setEditVoucher(null)}
                handleSubmit={handleUpdateVoucher}
                initialValues={editVoucher || {}}
                isEdit={true}
            />
            {deleteVoucherId && (
                <CancelModal
                    visibleModal={!!deleteVoucherId}
                    setVisibleModal={() => setDeleteVoucherId(null)}
                    handleClose={handleDeleteVoucher}
                    text="This voucher code body will be deleted, are you sure?"
                    header="Delete voucher code body"
                />
            )}
            {isOpenInvalidEmailsModal &&
                <InfoModal
                    visibleModal={isOpenInvalidEmailsModal}
                    close={() => {
                        setIsOpenInvalidEmailsModal(false)
                        setInvalidEmailsList([]);
                    }}
                    header="List of invalid emails"
                    buttonText="Ok"
                    modalClassName="activate-code__invalid-emails-modal"
                >
                    <ul className="activate-code__invalid-emails-list">
                        {invalidEmailsList.map((email) => <li key={email}>{email}</li>)}
                    </ul>
                </InfoModal>
            }
        </div>
    )
}

export default Voucher;
